import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Descriptions, Empty, Form, Input, Modal, Radio, Row, Space, Spin, Steps, Table, Tag, Tooltip, Typography } from 'antd';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { saasSelfService } from '../services/api';
import {
  buildTenantLifecycleSteps,
  findActiveScheduledCancelRequest,
  getRequestCreationAvailability,
  isRevocableScheduledCancelRequest,
  isScheduledCancelRequest,
  mapSubmitErrorToUserMessage,
  normalizeFulfillment,
  resolveCreateRequestSuccessFeedback,
  resolveOperationalStage,
} from './TenantAccountSubscriptionPage.utils';

const { Title, Text } = Typography;

const STATUS_COLOR = {
  trialing: 'blue',
  active: 'green',
  grace: 'gold',
  suspended: 'red',
  cancelled: 'default',
  none: 'default',
};

const STATUS_LABEL = {
  trialing: 'TRIAL',
  active: 'ACTIVE',
  grace: 'GRACE',
  suspended: 'SUSPENDED',
  cancelled: 'CANCELLED',
  none: 'SEM ASSINATURA',
};

const CHANGE_REQUEST_LABEL = {
  UPGRADE: 'Upgrade',
  DOWNGRADE: 'Downgrade',
  CANCEL: 'Cancelamento',
};

const BILLING_TYPE_LABEL = {
  PIX: 'PIX',
  BOLETO: 'Boleto',
};

const PAYMENT_METHOD_LABEL = {
  pix: 'PIX',
  boleto: 'Boleto',
  card: 'Cartao',
  unknown: 'Nao informado',
};

const CHANGE_REQUEST_STATUS_COLOR = {
  PENDING: 'gold',
  APPROVED: 'green',
  REJECTED: 'red',
  CANCELLED: 'default',
};

const FULFILLMENT_COLOR = {
  NOT_STARTED: 'default',
  PAYMENT_IN_PROGRESS: 'processing',
  PAID_PENDING_APPLY: 'gold',
  EFFECTUATED: 'green',
  FAILED: 'red',
  NOOP_TERMINAL: 'default',
};

const FULFILLMENT_LABEL = {
  NOT_STARTED: 'Nao iniciado',
  PAYMENT_IN_PROGRESS: 'Pagamento em andamento',
  PAID_PENDING_APPLY: 'Pago aguardando aplicacao',
  EFFECTUATED: 'Efetivado',
  FAILED: 'Falhou',
  NOOP_TERMINAL: 'No-op terminal',
};

const PAYMENT_STATE_COLOR = {
  not_started: 'default',
  pending_provider_setup: 'processing',
  pending_payment: 'gold',
  paid: 'green',
  overdue: 'red',
  failed: 'red',
  cancelled: 'default',
  refunded: 'cyan',
  payment_setup_failed: 'red',
  canceled: 'default',
};

const PAYMENT_STATE_LABEL = {
  not_started: 'Nao iniciado',
  pending_provider_setup: 'Cobranca em preparo',
  pending_payment: 'Aguardando pagamento',
  paid: 'Pagamento confirmado',
  overdue: 'Pagamento em atraso',
  failed: 'Pagamento falhou',
  cancelled: 'Cobranca cancelada',
  refunded: 'Cobranca estornada',
  payment_setup_failed: 'Falha ao preparar cobranca',
  canceled: 'Cobranca cancelada',
};

const CHARGE_STATUS_COLOR = {
  open: 'default',
  pending: 'gold',
  paid: 'green',
  overdue: 'red',
  failed: 'red',
  cancelled: 'default',
  canceled: 'default',
  refunded: 'cyan',
};

const CHARGE_STATUS_LABEL = {
  open: 'Aberta',
  pending: 'Pendente',
  paid: 'Paga',
  overdue: 'Em atraso',
  failed: 'Falha',
  cancelled: 'Cancelada',
  canceled: 'Cancelada',
  refunded: 'Estornada',
};

const PREPARATION_STATUS_COLOR = {
  prepared: 'processing',
  reused: 'blue',
  failed: 'red',
  skipped: 'gold',
};

const PREPARATION_STATUS_LABEL = {
  prepared: 'Pagamento preparado',
  reused: 'Pagamento preparado (reuso)',
  failed: 'Falha na preparacao',
  skipped: 'Preparacao pendente',
};

const EXECUTION_MODE_LABEL = {
  manual_only: 'Fluxo manual',
  auto_apply_local_fallback: 'Fallback local',
  auto_apply_skipped: 'Auto apply ignorado',
};

const NEXT_ACTION_LABEL = {
  await_payment_confirmation: 'Aguardando confirmacao de pagamento',
  await_payment_preparation: 'Aguardando preparacao de pagamento',
  manual_recovery_required: 'Recuperacao manual necessaria',
  payment_registered_wait_manual_transition: 'Pagamento registrado; aguardando transicao manual',
};

const WARNING_LABEL = {
  provider_real_not_enabled: 'Ambiente seguro/local - cobranca real ainda nao habilitada.',
  payment_preparation_pending: 'Pagamento ainda nao foi preparado para esta solicitacao.',
  payment_preparation_failed_follow_manual_review: 'Houve falha na preparacao. O time da plataforma deve revisar manualmente.',
  auto_prepare_failed_use_manual_prepare_payment: 'Preparacao automatica falhou e foi mantida para recuperacao manual.',
  request_type_not_eligible_for_auto_prepare: 'Este tipo de solicitacao nao usa preparacao automatica de pagamento.',
};

const PROVIDER_KEY_LABEL = {
  dry_run: 'Local seguro (sem cobranca real)',
  manual: 'Manual',
  asaas: 'ASAAS',
  asaas_billing: 'ASAAS',
};

const PAYMENT_LINK_STATUS_COLOR = {
  available: 'green',
  pending: 'gold',
  paid: 'green',
  failed: 'red',
  cancelled: 'default',
  unknown: 'default',
};

const PAYMENT_LINK_STATUS_LABEL = {
  available: 'Link disponivel',
  pending: 'Link pendente',
  paid: 'Pago',
  failed: 'Falha',
  cancelled: 'Cancelado',
  unknown: 'Indefinido',
};

const PAYMENT_NEXT_ACTION_LABEL = {
  realizar_pagamento: 'Pagamento disponivel para abertura.',
  aguardar_liberacao_pagamento: 'Pagamento real ainda nao liberado para este ambiente.',
  aguardar_aprovacao: 'Sua solicitacao ainda esta aguardando aprovacao.',
  acompanhar_ativacao_assinatura: 'Pagamento registrado. Acompanhe a ativacao da assinatura.',
  acompanhar_solicitacao: 'Acompanhe a evolucao da solicitacao com a plataforma.',
  aguardar_preparacao_pagamento: 'Pagamento ainda nao foi preparado para esta solicitacao.',
  aguardar_reprocessamento_financeiro: 'Fluxo financeiro em analise. Aguarde o reprocessamento.',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('pt-BR');
};

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString('pt-BR');
};

const formatCurrencyCents = (value, currency = 'BRL') => {
  const normalized = Number(value || 0);
  const safe = Number.isFinite(normalized) ? normalized : 0;
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: String(currency || 'BRL').toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(safe / 100);
  } catch (_err) {
    return `R$ ${(safe / 100).toFixed(2)}`;
  }
};

const getApiError = (requestError, fallback) => {
  const detail = requestError?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
    return detail.message;
  }
  return fallback;
};

const formatCompactRef = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '-';
  if (normalized.length <= 14) return normalized;
  return `${normalized.slice(0, 6)}...${normalized.slice(-6)}`;
};

const renderStatusTag = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  return <Tag color={STATUS_COLOR[normalized] || 'default'}>{STATUS_LABEL[normalized] || (normalized || '-')}</Tag>;
};

const renderPaymentStateTag = (state) => {
  const normalized = String(state || '').trim().toLowerCase();
  if (!normalized) return <Text type="secondary">-</Text>;
  return <Tag color={PAYMENT_STATE_COLOR[normalized] || 'default'}>{PAYMENT_STATE_LABEL[normalized] || normalized}</Tag>;
};

const renderPreparationStatusTag = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized) return <Text type="secondary">-</Text>;
  return <Tag color={PREPARATION_STATUS_COLOR[normalized] || 'default'}>{PREPARATION_STATUS_LABEL[normalized] || normalized}</Tag>;
};

const renderChargeStatusTag = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized) return <Text type="secondary">-</Text>;
  return <Tag color={CHARGE_STATUS_COLOR[normalized] || 'default'}>{CHARGE_STATUS_LABEL[normalized] || normalized}</Tag>;
};

const renderFulfillmentTag = (value) => {
  const normalized = normalizeFulfillment(value);
  if (!normalized) {
    return <Tag color="default">LEGACY_FALLBACK</Tag>;
  }
  return <Tag color={FULFILLMENT_COLOR[normalized] || 'default'}>{FULFILLMENT_LABEL[normalized] || normalized}</Tag>;
};

const getOperationalStageTag = (item) => {
  const { stage, source } = resolveOperationalStage(item);
  const map = {
    pending_approval: { color: 'gold', label: 'Aguardando aprovacao' },
    approved_awaiting_payment: { color: 'default', label: 'Aprovado aguardando pagamento' },
    approved_payment_in_progress: { color: 'processing', label: 'Aprovado com pagamento em andamento' },
    paid_pending_apply: { color: 'gold', label: 'Pago aguardando aplicacao do plano' },
    payment_prepared: { color: 'processing', label: 'Pagamento preparado' },
    payment_pending: { color: 'orange', label: 'Pagamento pendente' },
    payment_failed: { color: 'red', label: 'Pagamento com falha' },
    effectuated: { color: 'green', label: 'Upgrade efetivado' },
    noop_terminal: { color: 'default', label: 'Encerrado sem aplicacao (no-op)' },
    active_after_webhook_compatible: { color: 'green', label: 'Pago (compativel com webhook futuro)' },
    not_applicable: { color: 'default', label: '-' },
  };
  const resolved = map[stage] || map.not_applicable;
  const suffix = source === 'legacy' ? ' (fallback legado)' : '';
  return <Tag color={resolved.color}>{`${resolved.label}${suffix}`}</Tag>;
};

const getFinancialMessage = (item) => {
  const requestType = String(item?.request_type || '').trim().toUpperCase();
  if (requestType === 'DOWNGRADE') {
    const scheduledOn = String(item?.scheduled_effective_on || '').trim();
    if (String(item?.status || '').trim().toUpperCase() === 'PENDING') {
      return 'Solicitacao de downgrade aguardando aprovacao da plataforma.';
    }
    if (String(item?.status || '').trim().toUpperCase() === 'APPROVED') {
      return scheduledOn
        ? `Downgrade agendado para ${scheduledOn}. O plano atual permanece ativo ate esta data.`
        : 'Downgrade aprovado e agendado para o proximo ciclo.';
    }
  }

  const { stage, source } = resolveOperationalStage(item);
  if (stage === 'pending_approval') return 'Solicitacao aguardando aprovacao da plataforma.';
  if (stage === 'approved_awaiting_payment') return 'Solicitacao aprovada; pagamento ainda em preparacao.';
  if (stage === 'approved_payment_in_progress') return 'Solicitacao aprovada e em execucao financeira (PAYMENT_IN_PROGRESS).';
  if (stage === 'paid_pending_apply') return 'Pagamento confirmado e aguardando aplicacao do upgrade (PAID_PENDING_APPLY).';
  if (stage === 'effectuated') return 'Upgrade efetivado com sucesso (EFFECTUATED).';
  if (stage === 'noop_terminal') return 'Fluxo encerrado sem aplicacao (NOOP_TERMINAL).';
  if (stage === 'payment_prepared') return 'Aguardando pagamento para ativacao da alteracao de plano.';
  if (stage === 'payment_pending') return 'Aguardando pagamento para ativacao da alteracao de plano.';
  if (stage === 'payment_failed') return 'Falha no fluxo financeiro. A plataforma deve revisar manualmente.';
  if (stage === 'active_after_webhook_compatible') return 'Pagamento registrado; ativacao ocorrera no fluxo de webhook quando habilitado.';
  if (source === 'legacy') return 'Estado operacional resolvido por fallback legado.';
  return '-';
};

const getWarningMessages = (item) => {
  const warnings = Array.isArray(item?.warnings) ? item.warnings : [];
  return warnings.map((warning) => WARNING_LABEL[warning] || 'Acompanhamento em analise pela plataforma.');
};

const resolveCancelScheduleStatus = (item) => {
  const requestType = String(item?.request_type || '').trim().toUpperCase();
  if (requestType !== 'CANCEL') {
    return '-';
  }

  const status = String(item?.status || '').trim().toUpperCase();
  const fulfillment = String(item?.fulfillment || '').trim().toUpperCase();

  if (status === 'CANCELLED' && fulfillment === 'NOOP_TERMINAL') {
    return 'Revogado';
  }

  if (item?.scheduled_processed_at) {
    return 'Processado';
  }

  if (isScheduledCancelRequest(item)) {
    return 'Cancelamento agendado';
  }

  return '-';
};

const renderPaymentLinkStatusTag = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized) return <Text type="secondary">-</Text>;
  return <Tag color={PAYMENT_LINK_STATUS_COLOR[normalized] || 'default'}>{PAYMENT_LINK_STATUS_LABEL[normalized] || normalized}</Tag>;
};

const resolvePaymentWarningMessages = (paymentInfo) => {
  const warnings = Array.isArray(paymentInfo?.warnings) ? paymentInfo.warnings : [];
  if (warnings.length === 0) return [];

  return warnings.map((warning) => {
    const normalized = String(warning || '').toLowerCase();
    if (normalized.includes('ambiente seguro')) {
      return 'Pagamento real ainda nao liberado para este ambiente.';
    }
    if (normalized.includes('aguardando aprovacao')) {
      return 'Sua solicitacao ainda esta aguardando aprovacao.';
    }
    if (normalized.includes('expir')) {
      return 'O link de pagamento expirou. Aguarde a disponibilizacao de um novo link.';
    }
    return warning;
  });
};

const getPaymentSummaryMessage = (paymentInfo) => {
  if (!paymentInfo) return '-';
  if (paymentInfo.payment_available && paymentInfo.payment_url) {
    return 'Pagamento disponivel para abertura em nova aba.';
  }
  if (paymentInfo.payment_available && paymentInfo.pix_payload) {
    return 'Pagamento PIX disponivel. Use o codigo copia e cola para concluir.';
  }

  const warningMessages = resolvePaymentWarningMessages(paymentInfo);
  if (warningMessages.length > 0) return warningMessages[0];

  if (!paymentInfo.charge_id) {
    return 'Pagamento ainda nao foi preparado para esta solicitacao.';
  }
  if (!paymentInfo.payment_url) {
    return 'Link de pagamento indisponivel no momento.';
  }
  return PAYMENT_NEXT_ACTION_LABEL[paymentInfo.next_action] || 'Acompanhe as orientacoes da plataforma.';
};

const resolvePreferredPaymentLink = (paymentInfo) => {
  const url = String(paymentInfo?.payment_url || '').trim();
  if (url) return url;
  const boleto = String(paymentInfo?.boleto_url || '').trim();
  if (boleto) return boleto;
  const invoice = String(paymentInfo?.invoice_url || '').trim();
  if (invoice) return invoice;
  return '';
};

const resolvePixQrImageSrc = (paymentInfo) => {
  const raw = String(paymentInfo?.pix_qr_code || '').trim();
  if (!raw) return '';
  if (raw.startsWith('data:image/')) return raw;
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  if (raw.length > 100) return `data:image/png;base64,${raw}`;
  return '';
};

const isPendingSubscriptionPayment = (row) => {
  const chargeStatus = String(row?.charge_status || '').trim().toLowerCase();
  const paymentState = String(row?.payment_state || '').trim().toLowerCase();
  if (chargeStatus === 'pending' || chargeStatus === 'overdue') return true;
  if (paymentState === 'pending_payment' || paymentState === 'pending_provider_setup') return true;
  return false;
};

const TenantAccountSubscriptionPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [requests, setRequests] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successDescription, setSuccessDescription] = useState('');
  const [requestType, setRequestType] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paymentByRequestId, setPaymentByRequestId] = useState({});
  const [paymentLoadingByRequestId, setPaymentLoadingByRequestId] = useState({});
  const [paymentErrorByRequestId, setPaymentErrorByRequestId] = useState({});
  const [upgradePlans, setUpgradePlans] = useState([]);
  const [upgradePlansLoading, setUpgradePlansLoading] = useState(false);
  const [upgradePlansError, setUpgradePlansError] = useState('');
  const [downgradePlans, setDowngradePlans] = useState([]);
  const [downgradePlansLoading, setDowngradePlansLoading] = useState(false);
  const [downgradePlansError, setDowngradePlansError] = useState('');
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
  const [paymentHistoryError, setPaymentHistoryError] = useState('');
  const [immediatePaymentModal, setImmediatePaymentModal] = useState({ open: false, requestId: null, payment: null, historyItem: null });
  const [revokeLoadingByRequestId, setRevokeLoadingByRequestId] = useState({});
  const [paymentRefreshLoading, setPaymentRefreshLoading] = useState(false);
  const [form] = Form.useForm();

  const requestCreationAvailability = useMemo(
    () => getRequestCreationAvailability(subscription, requests, { upgradePlans, downgradePlans }),
    [subscription, requests, upgradePlans, downgradePlans]
  );

  const activeScheduledCancelRequest = useMemo(
    () => findActiveScheduledCancelRequest(requests),
    [requests]
  );

  const highlightedPendingPayment = useMemo(() => {
    const rows = Array.isArray(paymentHistory) ? paymentHistory : [];
    const pendingRows = rows.filter((row) => {
      const requestType = String(row?.request_type || '').trim().toUpperCase();
      return requestType === 'UPGRADE' && isPendingSubscriptionPayment(row);
    });
    return pendingRows.length > 0 ? pendingRows[0] : null;
  }, [paymentHistory]);

  const immediatePaymentInfo = useMemo(() => {
    const payment = immediatePaymentModal.payment || {};
    const historyItem = immediatePaymentModal.historyItem || {};
    return {
      ...historyItem,
      ...payment,
      amount_cents: payment.amount_cents ?? historyItem.amount_cents ?? null,
      currency_code: payment.currency_code || historyItem.currency_code || 'BRL',
      due_at: payment.due_at || historyItem.due_at || null,
      billing_type: payment.billing_type || historyItem.billing_type || null,
      payment_method: payment.payment_method || historyItem.payment_method || null,
      payment_state: payment.payment_state || historyItem.payment_state || null,
      charge_status: payment.charge_status || historyItem.charge_status || null,
      pix_payload: payment.pix_payload || historyItem.pix_payload || '',
      pix_qr_code: payment.pix_qr_code || historyItem.pix_qr_code || '',
      boleto_url: payment.boleto_url || historyItem.boleto_url || '',
      payment_url: payment.payment_url || historyItem.payment_url || '',
      invoice_url: payment.invoice_url || historyItem.invoice_url || '',
      provider_payment_ref: payment.provider_payment_ref || historyItem.provider_payment_ref || '',
      next_action: payment.next_action || historyItem.next_action || '',
    };
  }, [immediatePaymentModal]);

  const loadPaymentDetails = useCallback(async (items) => {
    const ids = (Array.isArray(items) ? items : [])
      .map((item) => String(item?.id || '').trim())
      .filter(Boolean);

    if (ids.length === 0) {
      setPaymentByRequestId({});
      setPaymentLoadingByRequestId({});
      setPaymentErrorByRequestId({});
      return;
    }

    const loadingMap = Object.fromEntries(ids.map((id) => [id, true]));
    setPaymentLoadingByRequestId(loadingMap);
    setPaymentErrorByRequestId({});

    const responses = await Promise.all(
      ids.map(async (requestId) => {
        try {
          const response = await saasSelfService.getChangeRequestPayment(requestId);
          return { requestId, data: response?.data || null, error: '' };
        } catch (requestError) {
          return {
            requestId,
            data: null,
            error: getApiError(requestError, 'Nao foi possivel consultar os dados de pagamento desta solicitacao.'),
          };
        }
      })
    );

    const nextDataMap = {};
    const nextErrorMap = {};
    const nextLoadingMap = {};

    responses.forEach(({ requestId, data, error: paymentError }) => {
      nextLoadingMap[requestId] = false;
      if (paymentError) {
        nextErrorMap[requestId] = paymentError;
        return;
      }
      nextDataMap[requestId] = data;
    });

    setPaymentByRequestId(nextDataMap);
    setPaymentErrorByRequestId(nextErrorMap);
    setPaymentLoadingByRequestId(nextLoadingMap);
  }, []);

  const loadData = useCallback(async () => {
    const [subscriptionRes, requestsRes, upgradePlansResult, downgradePlansResult, paymentHistoryResult] = await Promise.all([
      saasSelfService.getSubscription(),
      saasSelfService.listChangeRequests(),
      saasSelfService.listUpgradePlans().then(
        (response) => ({ ok: true, data: response?.data }),
        () => ({ ok: false, data: [] })
      ),
      saasSelfService.listDowngradePlans().then(
        (response) => ({ ok: true, data: response?.data }),
        () => ({ ok: false, data: [] })
      ),
      saasSelfService.listPaymentHistory().then(
        (response) => ({ ok: true, data: response?.data }),
        () => ({ ok: false, data: [] })
      ),
    ]);
    const nextRequests = Array.isArray(requestsRes?.data) ? requestsRes.data : [];
    const nextUpgradePlans = Array.isArray(upgradePlansResult?.data) ? upgradePlansResult.data : [];
    const nextDowngradePlans = Array.isArray(downgradePlansResult?.data) ? downgradePlansResult.data : [];
    const nextPaymentHistory = Array.isArray(paymentHistoryResult?.data) ? paymentHistoryResult.data : [];
    setSubscription(subscriptionRes?.data || null);
    setRequests(nextRequests);
    setUpgradePlans(nextUpgradePlans);
    setDowngradePlans(nextDowngradePlans);
    setPaymentHistory(nextPaymentHistory);
    setUpgradePlansError(upgradePlansResult?.ok ? '' : 'Nao foi possivel carregar os planos disponiveis para upgrade.');
    setDowngradePlansError(downgradePlansResult?.ok ? '' : 'Nao foi possivel carregar os planos disponiveis para downgrade.');
    setPaymentHistoryError(paymentHistoryResult?.ok ? '' : 'Nao foi possivel carregar o historico de pagamentos da assinatura.');
    await loadPaymentDetails(nextRequests);
  }, [loadPaymentDetails]);

  const loadSinglePaymentDetail = useCallback(async (requestId) => {
    const normalizedId = String(requestId || '').trim();
    if (!normalizedId) {
      return;
    }

    setPaymentLoadingByRequestId((prev) => ({ ...prev, [normalizedId]: true }));
    setPaymentErrorByRequestId((prev) => {
      const next = { ...prev };
      delete next[normalizedId];
      return next;
    });

    try {
      const response = await saasSelfService.getChangeRequestPayment(normalizedId);
      setPaymentByRequestId((prev) => ({ ...prev, [normalizedId]: response?.data || null }));
    } catch (requestError) {
      setPaymentErrorByRequestId((prev) => ({
        ...prev,
        [normalizedId]: getApiError(requestError, 'Nao foi possivel consultar os dados de pagamento desta solicitacao.'),
      }));
    } finally {
      setPaymentLoadingByRequestId((prev) => ({ ...prev, [normalizedId]: false }));
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        setUpgradePlansLoading(true);
        setDowngradePlansLoading(true);
        setPaymentHistoryLoading(true);
        setUpgradePlansError('');
        setDowngradePlansError('');
        setPaymentHistoryError('');
        await loadData();

        if (!mounted) return;
      } catch (requestError) {
        if (!mounted) return;
        setError(getApiError(requestError, 'Nao foi possivel carregar sua assinatura SaaS no momento.'));
      } finally {
        if (mounted) {
          setLoading(false);
          setUpgradePlansLoading(false);
          setDowngradePlansLoading(false);
          setPaymentHistoryLoading(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [loadData]);

  const tenantInfo = useMemo(() => {
    const currentTenant = user?.current_tenant;
    return {
      id: user?.current_tenant_id || currentTenant?.tenant_id || '-',
      nome: currentTenant?.tenant_name || currentTenant?.nome || '-',
      slug: currentTenant?.tenant_slug || currentTenant?.slug || '-',
      source: user?.tenant_resolution_source || '-',
    };
  }, [user]);

  const hasSubscription = Boolean(subscription?.has_subscription);
  const isSubscriptionCancelled = String(subscription?.status || '').trim().toLowerCase() === 'cancelled';

  const openRequestModal = (type) => {
    const normalizedType = String(type || '').trim().toUpperCase();
    setRequestType(type);
    setIsModalOpen(true);
    form.resetFields();
    form.setFieldsValue({
      request_type: type,
      requested_plan_code: (
        normalizedType === 'UPGRADE' && upgradePlans.length > 0
          ? String(upgradePlans[0]?.code || '').trim()
          : normalizedType === 'DOWNGRADE' && downgradePlans.length > 0
            ? String(downgradePlans[0]?.code || '').trim()
            : ''
      ) || undefined,
      billing_type: normalizedType === 'UPGRADE' ? 'BOLETO' : undefined,
      reason: undefined,
    });
  };

  const closeRequestModal = () => {
    setIsModalOpen(false);
    setRequestType(null);
    form.resetFields();
  };

  const closeImmediatePaymentModal = () => {
    setImmediatePaymentModal({ open: false, requestId: null, payment: null, historyItem: null });
  };

  const refreshPaymentStatusByRequestId = useCallback(async (requestId) => {
    const normalizedId = String(requestId || '').trim();
    if (!normalizedId) {
      await loadData();
      return;
    }

    setPaymentRefreshLoading(true);
    try {
      const [paymentResponse, historyResponse] = await Promise.all([
        saasSelfService.refreshChangeRequestPayment(normalizedId).catch(() => ({ data: null })),
        saasSelfService.listPaymentHistory().catch(() => ({ data: [] })),
      ]);
      const refreshedPayment = paymentResponse?.data || null;
      const historyRows = Array.isArray(historyResponse?.data) ? historyResponse.data : [];
      const historyItem = historyRows.find((row) => String(row?.request_id || '').trim() === normalizedId) || null;

      setPaymentByRequestId((prev) => ({
        ...prev,
        [normalizedId]: refreshedPayment || prev[normalizedId] || null,
      }));
      setPaymentHistory(historyRows);

      if (String(immediatePaymentModal.requestId || '').trim() === normalizedId) {
        setImmediatePaymentModal((prev) => ({
          ...prev,
          payment: refreshedPayment || prev.payment,
          historyItem,
        }));
      }

      await loadData();
    } catch (_error) {
      await loadData();
    } finally {
      setPaymentRefreshLoading(false);
    }
  }, [immediatePaymentModal.requestId, loadData]);

  const refreshImmediatePaymentModal = async () => {
    const requestId = String(immediatePaymentModal.requestId || '').trim();
    if (!requestId) return;
    await refreshPaymentStatusByRequestId(requestId);
  };

  const submitRequest = async (values) => {
    setSubmitLoading(true);
    setError('');
    setSuccessMessage('');
    setSuccessDescription('');
    try {
      const normalizedType = String(values?.request_type || '').trim().toUpperCase();
      const payload = {
        request_type: normalizedType,
        reason: values?.reason || null,
      };

      if (normalizedType === 'UPGRADE' || normalizedType === 'DOWNGRADE') {
        payload.requested_plan_code = values?.requested_plan_code || null;
      } else {
        payload.requested_plan_code = null;
      }

      if (normalizedType === 'UPGRADE') {
        payload.billing_type = String(values?.billing_type || 'BOLETO').trim().toUpperCase();
      }

      const createResponse = await saasSelfService.createChangeRequest(payload);
      const createdRequest = createResponse?.data || null;
      const createdRequestId = String(createdRequest?.id || '').trim();

      let immediatePayment = null;
      if (normalizedType === 'UPGRADE' && createdRequestId) {
        try {
          const paymentResponse = await saasSelfService.getChangeRequestPayment(createdRequestId);
          immediatePayment = paymentResponse?.data || null;
        } catch (_paymentError) {
          immediatePayment = null;
        }
      }

      await loadData();
      closeRequestModal();
      const feedback = resolveCreateRequestSuccessFeedback(createdRequest);
      setSuccessMessage(feedback.message);
      setSuccessDescription(feedback.description);

      if (normalizedType === 'UPGRADE' && createdRequestId) {
        let historyItem = null;
        try {
          const historyResponse = await saasSelfService.listPaymentHistory();
          const rows = Array.isArray(historyResponse?.data) ? historyResponse.data : [];
          historyItem = rows.find((row) => String(row?.request_id || '').trim() === createdRequestId) || null;
        } catch (_historyError) {
          historyItem = null;
        }
        setImmediatePaymentModal({
          open: true,
          requestId: createdRequestId,
          payment: immediatePayment,
          historyItem,
        });
      }
    } catch (requestError) {
      const detail = getApiError(requestError, 'Nao foi possivel enviar sua solicitacao.');
      setError(mapSubmitErrorToUserMessage(detail, 'Nao foi possivel enviar sua solicitacao.'));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleRevokeScheduledCancel = useCallback(async (requestId) => {
    const normalizedId = String(requestId || '').trim();
    if (!normalizedId) {
      return;
    }

    setRevokeLoadingByRequestId((prev) => ({ ...prev, [normalizedId]: true }));
    setError('');
    setSuccessMessage('');
    setSuccessDescription('');

    try {
      await saasSelfService.revokeChangeRequest(normalizedId);
      await loadData();
      setSuccessMessage('Cancelamento agendado desfeito com sucesso.');
      setSuccessDescription('A assinatura permanece ativa e os dados continuam preservados conforme politica de retencao e auditoria.');
    } catch (requestError) {
      setError(getApiError(requestError, 'Nao foi possivel desfazer o cancelamento agendado.'));
    } finally {
      setRevokeLoadingByRequestId((prev) => ({ ...prev, [normalizedId]: false }));
    }
  }, [loadData]);

  const handleOpenPayment = (paymentInfo) => {
    const paymentUrl = resolvePreferredPaymentLink(paymentInfo);
    if (!paymentInfo?.payment_available || !paymentUrl) {
      return;
    }
    window.open(paymentUrl, '_blank', 'noopener,noreferrer');
  };

  const handleOpenAnyPaymentLink = (rawUrl) => {
    const paymentUrl = String(rawUrl || '').trim();
    if (!paymentUrl) return;
    window.open(paymentUrl, '_blank', 'noopener,noreferrer');
  };

  const handleCopyPixPayload = async (value) => {
    const pixPayload = String(value || '').trim();
    if (!pixPayload) {
      return;
    }
    try {
      await navigator.clipboard.writeText(pixPayload);
      Modal.success({
        title: 'PIX copiado',
        content: 'Codigo copia e cola do PIX foi copiado para a area de transferencia.',
      });
    } catch (_error) {
      Modal.warning({
        title: 'Falha ao copiar PIX',
        content: 'Nao foi possivel copiar automaticamente. Copie manualmente o codigo exibido.',
      });
    }
  };

  if (user?.is_platform_admin === true) {
    return <Navigate to="/acesso-negado" replace />;
  }

  if (loading) {
    return (
      <div style={{ minHeight: '70vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" tip="Carregando assinatura..." />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ marginBottom: 0 }}>Minha Assinatura</Title>
          <Text type="secondary">Portal SaaS self-service para upgrades, pagamentos e acompanhamento.</Text>
        </Col>
      </Row>

      {error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Falha ao carregar assinatura"
          description={error}
        />
      ) : null}

      {successMessage ? (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          message={successMessage}
          description={successDescription || 'A solicitacao foi registrada com sucesso.'}
        />
      ) : null}

      {isSubscriptionCancelled ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Assinatura cancelada"
          description="Assinatura cancelada. Os dados permanecem preservados conforme politica de retencao e auditoria."
        />
      ) : null}

      {activeScheduledCancelRequest ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Cancelamento agendado para ${formatDate(activeScheduledCancelRequest.scheduled_effective_on)}.`}
          description="Seu acesso permanece ativo ate a data de efetivacao. Tenant, usuarios e dados operacionais nao sao apagados automaticamente."
        />
      ) : null}

      {highlightedPendingPayment ? (
        <Card
          size="small"
          style={{ marginBottom: 16, borderColor: '#faad14' }}
          title="Pagamento pendente da sua solicitacao de upgrade"
          extra={(
            <Button
              size="small"
              onClick={() => refreshPaymentStatusByRequestId(highlightedPendingPayment.request_id)}
              loading={loading || paymentHistoryLoading || paymentRefreshLoading}
            >
              Ja paguei / atualizar status
            </Button>
          )}
        >
          <Row gutter={[12, 12]}>
            <Col xs={24} md={16}>
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="Status">{renderChargeStatusTag(highlightedPendingPayment.charge_status)}</Descriptions.Item>
                <Descriptions.Item label="Metodo">
                  {BILLING_TYPE_LABEL[String(highlightedPendingPayment.billing_type || '').trim().toUpperCase()] || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Valor">
                  {highlightedPendingPayment.amount_cents != null
                    ? formatCurrencyCents(highlightedPendingPayment.amount_cents, highlightedPendingPayment.currency_code || 'BRL')
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Vencimento">{formatDateTime(highlightedPendingPayment.due_at)}</Descriptions.Item>
              </Descriptions>

              <Alert
                type="info"
                showIcon
                style={{ marginTop: 12 }}
                message="Seu plano atual permanece ativo ate a confirmacao do pagamento."
              />
            </Col>
            <Col xs={24} md={8}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  type="primary"
                  disabled={!resolvePreferredPaymentLink(highlightedPendingPayment)}
                  onClick={() => handleOpenAnyPaymentLink(resolvePreferredPaymentLink(highlightedPendingPayment))}
                  block
                >
                  Abrir boleto/link
                </Button>
                <Button
                  disabled={!highlightedPendingPayment.pix_payload}
                  onClick={() => handleCopyPixPayload(highlightedPendingPayment.pix_payload)}
                  block
                >
                  Copiar PIX
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      ) : null}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Plano Atual" size="small">
            {!hasSubscription ? (
              <Empty description="Sem assinatura corrente para este tenant." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="Plano atual">
                  {subscription.plan_name || subscription.plan_code || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Codigo do plano">{subscription.plan_code || '-'}</Descriptions.Item>
                <Descriptions.Item label="Valor">
                  {subscription.plan_base_price_cents != null
                    ? formatCurrencyCents(subscription.plan_base_price_cents, subscription.currency_code || 'BRL')
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Ciclo">
                  {subscription.billing_period_months ? `${subscription.billing_period_months} mes(es)` : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Recursos">
                  {Array.isArray(subscription.plan_highlights) && subscription.plan_highlights.length > 0 ? (
                    <Space wrap>
                      {subscription.plan_highlights.slice(0, 6).map((item) => (
                        <Tag key={`plan-highlight-${item}`} color="blue">{item}</Tag>
                      ))}
                    </Space>
                  ) : (
                    <Text type="secondary">-</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Status da assinatura">
                  {renderStatusTag(subscription.status)}
                </Descriptions.Item>
                <Descriptions.Item label="Período atual">
                  {subscription.current_period_start || '-'} até {subscription.current_period_end || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Proxima renovacao">
                  {subscription.current_period_end || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Fim do trial">
                  {formatDateTime(subscription.trial_ends_at)}
                </Descriptions.Item>
                <Descriptions.Item label="Billing provider">
                  {subscription.billing_provider || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Provider subscription id">
                  {formatCompactRef(subscription.provider_subscription_id)}
                </Descriptions.Item>
                <Descriptions.Item label="Ultima alteracao">
                  {formatDateTime(subscription.last_transition_at)}
                </Descriptions.Item>
                <Descriptions.Item label="Origem da alteracao">
                  {subscription.last_transition_source || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Motivo da alteracao">
                  {subscription.last_transition_reason_code || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Transition version">
                  {subscription.transition_version ?? '-'}
                </Descriptions.Item>
              </Descriptions>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Dados do tenant" size="small">
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Tenant ID">{tenantInfo.id}</Descriptions.Item>
              <Descriptions.Item label="Nome">{tenantInfo.nome}</Descriptions.Item>
              <Descriptions.Item label="Slug">{tenantInfo.slug}</Descriptions.Item>
              <Descriptions.Item label="Origem do contexto">{tenantInfo.source}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24}>
          <Card title="Solicitar alteracao" size="small">
            <Text type="secondary">
              Para upgrade de plano padrao, a cobranca e criada automaticamente e a mudanca e aplicada apos confirmacao de pagamento.
            </Text>
            {!requestCreationAvailability.canUpgrade ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
                message="Solicitacao de upgrade temporariamente bloqueada"
                description={requestCreationAvailability.upgradeReason}
              />
            ) : null}
            {requestCreationAvailability.canUpgrade && !upgradePlansLoading && upgradePlans.length === 0 ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
                message="Nenhum plano elegivel para upgrade"
                description={upgradePlansError || 'No momento, nao ha planos padrao/publicos disponiveis para upgrade desta assinatura.'}
              />
            ) : null}
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 12 }}
              message="Autosservico de alteracoes"
              description="Upgrade padrao segue fluxo 100% autosservico com pagamento imediato. Downgrade e cancelamento continuam agendados para o proximo ciclo elegivel."
            />
            {requestCreationAvailability.cancelRetentionNotice ? (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 12 }}
                message="Politica de preservacao de dados"
                description={requestCreationAvailability.cancelRetentionNotice}
              />
            ) : null}
            <div style={{ marginTop: 12 }}>
              <Space wrap>
                <Tooltip title={requestCreationAvailability.canUpgrade ? 'Abrir nova solicitacao de upgrade.' : requestCreationAvailability.upgradeReason}>
                  <span>
                    <Button
                      disabled={!requestCreationAvailability.canUpgrade || upgradePlansLoading || upgradePlans.length === 0}
                      onClick={() => openRequestModal('UPGRADE')}
                    >
                      Solicitar upgrade
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={requestCreationAvailability.downgradeReason}>
                  <span>
                    <Button
                      disabled={!requestCreationAvailability.canDowngrade || downgradePlansLoading || downgradePlans.length === 0}
                      onClick={() => openRequestModal('DOWNGRADE')}
                    >
                      Solicitar downgrade
                    </Button>
                  </span>
                </Tooltip>
                {!isSubscriptionCancelled ? (
                  <Tooltip title={requestCreationAvailability.canCancel ? 'Solicitar cancelamento agendado da assinatura.' : requestCreationAvailability.cancelReason}>
                    <span>
                      <Button danger disabled={!requestCreationAvailability.canCancel} onClick={() => openRequestModal('CANCEL')}>
                        Solicitar cancelamento
                      </Button>
                    </span>
                  </Tooltip>
                ) : null}
              </Space>
            </div>
          </Card>
        </Col>

        <Col xs={24}>
          <Card
            title="Historico Financeiro da Assinatura"
            size="small"
            extra={(
              <Button size="small" loading={paymentHistoryLoading} onClick={loadData}>
                Recarregar historico
              </Button>
            )}
          >
            {paymentHistoryError ? (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={paymentHistoryError} />
            ) : null}

            <Table
              size="small"
              rowKey={(row) => `${row.charge_id || 'intent'}-${row.payment_intent_id || row.request_id || row.created_at || 'row'}`}
              loading={paymentHistoryLoading}
              dataSource={Array.isArray(paymentHistory) ? paymentHistory : []}
              pagination={{ pageSize: 8 }}
              locale={{ emptyText: 'Nenhuma cobranca de assinatura encontrada para este tenant.' }}
              columns={[
                {
                  title: 'Criada em',
                  dataIndex: 'created_at',
                  key: 'created_at',
                  render: (value) => formatDateTime(value),
                },
                {
                  title: 'Solicitacao',
                  key: 'request',
                  render: (_, row) => (
                    <Space direction="vertical" size={0}>
                      <Text>{CHANGE_REQUEST_LABEL[String(row?.request_type || '').trim().toUpperCase()] || row?.request_type || '-'}</Text>
                      <Text type="secondary">{row?.description || row?.request_id || '-'}</Text>
                    </Space>
                  ),
                },
                {
                  title: 'Valor',
                  key: 'amount',
                  render: (_, row) => (
                    row?.amount_cents != null
                      ? formatCurrencyCents(row.amount_cents, row?.currency_code || 'BRL')
                      : '-'
                  ),
                },
                {
                  title: 'Vencimento',
                  dataIndex: 'due_at',
                  key: 'due_at',
                  render: (value) => formatDateTime(value),
                },
                {
                  title: 'Metodo',
                  key: 'method',
                  render: (_, row) => (
                    <Space direction="vertical" size={0}>
                      <Text>{BILLING_TYPE_LABEL[String(row?.billing_type || '').trim().toUpperCase()] || '-'}</Text>
                      <Text type="secondary">{PAYMENT_METHOD_LABEL[String(row?.payment_method || '').trim().toLowerCase()] || '-'}</Text>
                    </Space>
                  ),
                },
                {
                  title: 'Status',
                  key: 'status',
                  render: (_, row) => (
                    <Space direction="vertical" size={4}>
                      {renderChargeStatusTag(row?.charge_status)}
                      {renderPaymentStateTag(row?.payment_state)}
                    </Space>
                  ),
                },
                {
                  title: 'Acoes',
                  key: 'actions',
                  render: (_, row) => {
                    const paymentLink = resolvePreferredPaymentLink(row);
                    return (
                      <Space wrap>
                        <Button size="small" disabled={!paymentLink} onClick={() => handleOpenAnyPaymentLink(paymentLink)}>
                          Abrir
                        </Button>
                        <Button
                          size="small"
                          disabled={!row?.pix_payload}
                          onClick={() => handleCopyPixPayload(row?.pix_payload)}
                        >
                          Copiar PIX
                        </Button>
                      </Space>
                    );
                  },
                },
              ]}
            />
          </Card>
        </Col>

        <Col xs={24}>
          <Card title="Solicitacoes de Alteracao" size="small">
            {requests.length === 0 ? (
              <Empty description="Nenhuma solicitacao registrada para este tenant." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                {requests.map((item) => {
                  const isCancelRequest = String(item?.request_type || '').trim().toUpperCase() === 'CANCEL';
                  const isRevocableCancel = isRevocableScheduledCancelRequest(item);
                  const cancelScheduleStatus = resolveCancelScheduleStatus(item);

                  return (
                  <Card key={item.id} size="small" type="inner">
                    {isCancelRequest && cancelScheduleStatus !== '-' ? (
                      <Alert
                        type={cancelScheduleStatus === 'Revogado' ? 'warning' : cancelScheduleStatus === 'Processado' ? 'success' : 'info'}
                        showIcon
                        style={{ marginBottom: 12 }}
                        message={cancelScheduleStatus}
                        description={item?.scheduled_effective_on
                          ? `Data prevista de efetivacao: ${formatDate(item.scheduled_effective_on)}.`
                          : 'Cancelamento agendado para o proximo ciclo elegivel.'}
                      />
                    ) : null}
                    {(() => {
                      const stage = resolveOperationalStage(item).stage;
                      const stageColor = stage === 'effectuated'
                        ? 'green'
                        : stage === 'payment_failed' || stage === 'noop_terminal'
                          ? 'red'
                          : 'blue';
                      return (
                        <Space wrap style={{ marginBottom: 12 }}>
                          <Tag color={stageColor}>request_id: {item.id}</Tag>
                          <Tag color="default">status: {item.status || '-'}</Tag>
                          <Tag color="default">fulfillment: {item.fulfillment || '-'}</Tag>
                        </Space>
                      );
                    })()}
                    {paymentErrorByRequestId[item.id] ? (
                      <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="Falha ao consultar pagamento"
                        description={paymentErrorByRequestId[item.id]}
                      />
                    ) : null}
                    {item.status === 'APPROVED' ? (
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="Aprovado comercialmente"
                        description={getFinancialMessage(item)}
                      />
                    ) : null}
                    {item.status === 'APPROVED' && item.provider_real_enabled === false ? (
                      <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="Ambiente seguro/local"
                        description="Ambiente seguro/local - cobranca real ainda nao habilitada."
                      />
                    ) : null}
                    <Descriptions size="small" column={1}>
                      <Descriptions.Item label="Tipo">{CHANGE_REQUEST_LABEL[item.request_type] || item.request_type}</Descriptions.Item>
                      <Descriptions.Item label="Status">
                        <Tag color={CHANGE_REQUEST_STATUS_COLOR[item.status] || 'default'}>{item.status || '-'}</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Fulfillment">
                        {renderFulfillmentTag(item.fulfillment)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Reason code">{item.reason_code || '-'}</Descriptions.Item>
                      <Descriptions.Item label="No-op reason">{item.noop_reason || '-'}</Descriptions.Item>
                      <Descriptions.Item label="Estagio operacional">
                        {getOperationalStageTag(item)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Preparacao de pagamento">
                        {renderPreparationStatusTag(item.payment_preparation_status)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Status financeiro">
                        {renderPaymentStateTag(item.payment_state)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Status da cobranca">
                        {renderChargeStatusTag(item.charge_status)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Modo operacional">
                        {EXECUTION_MODE_LABEL[item.execution_mode] || item.execution_mode || '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Proxima acao">
                        {NEXT_ACTION_LABEL[item.next_action] || item.next_action || '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Mensagem financeira">
                        {getFinancialMessage(item)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Referencia do preparo">{formatCompactRef(item.payment_intent_id)}</Descriptions.Item>
                      <Descriptions.Item label="Referencia da cobranca">{formatCompactRef(item.charge_id)}</Descriptions.Item>
                      <Descriptions.Item label="Canal de cobranca">{PROVIDER_KEY_LABEL[item.provider_key] || '-'}</Descriptions.Item>
                      <Descriptions.Item label="Plano atual">{item.current_plan_code || '-'}</Descriptions.Item>
                      <Descriptions.Item label="Plano solicitado">{item.requested_plan_code || '-'}</Descriptions.Item>
                      <Descriptions.Item label="Forma de pagamento selecionada">
                        {BILLING_TYPE_LABEL[String(item.billing_type || '').trim().toUpperCase()] || '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Efetivacao agendada">
                        {item.scheduled_effective_on || '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Agendamento processado em">
                        {formatDateTime(item.scheduled_processed_at)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Status do agendamento">
                        {isCancelRequest
                          ? cancelScheduleStatus
                          : String(item.request_type || '').trim().toUpperCase() === 'DOWNGRADE' && String(item.status || '').trim().toUpperCase() === 'APPROVED' && String(item.fulfillment || '').trim().toUpperCase() === 'NOT_STARTED'
                            ? 'Downgrade agendado e ainda nao efetivado'
                            : '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Motivo">{item.reason || '-'}</Descriptions.Item>
                      <Descriptions.Item label="Solicitado em">{formatDateTime(item.created_at)}</Descriptions.Item>
                      <Descriptions.Item label="Vencimento da cobranca">{formatDateTime(item.payment_due_at)}</Descriptions.Item>
                      <Descriptions.Item label="Referencia externa">{formatCompactRef(item.provider_charge_ref)}</Descriptions.Item>
                      <Descriptions.Item label="Referencia interna">{formatCompactRef(item.billing_correlation_id)}</Descriptions.Item>
                      <Descriptions.Item label="Warnings operacionais">
                        {getWarningMessages(item).length > 0 ? (
                          <Space wrap>
                            {getWarningMessages(item).map((warning) => (
                              <Tag key={`${item.id}-${warning}`} color="gold">{warning}</Tag>
                            ))}
                          </Space>
                        ) : (
                          <Text type="secondary">-</Text>
                        )}
                      </Descriptions.Item>
                    </Descriptions>

                    {isRevocableCancel ? (
                      <div style={{ marginTop: 12 }}>
                        <Button
                          danger
                          loading={Boolean(revokeLoadingByRequestId[item.id])}
                          onClick={() => {
                            Modal.confirm({
                              title: 'Desfazer cancelamento agendado?',
                              content: (
                                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                  <Text>O acesso permanece ativo ate a data de efetivacao.</Text>
                                  <Text type="secondary">Tenant, usuarios e dados operacionais nao sao apagados automaticamente.</Text>
                                </Space>
                              ),
                              okText: 'Desfazer cancelamento',
                              cancelText: 'Manter agendamento',
                              okButtonProps: { danger: true },
                              onOk: () => handleRevokeScheduledCancel(item.id),
                            });
                          }}
                        >
                          Desfazer cancelamento
                        </Button>
                      </div>
                    ) : null}

                    <Card
                      title="Pagamento da alteracao"
                      size="small"
                      style={{ marginTop: 12 }}
                      bodyStyle={{ paddingBottom: 12 }}
                      extra={(
                        <Button
                          size="small"
                          loading={Boolean(paymentLoadingByRequestId[item.id])}
                          onClick={() => loadSinglePaymentDetail(item.id)}
                        >
                          Recarregar status
                        </Button>
                      )}
                    >
                      {paymentLoadingByRequestId[item.id] ? (
                        <Space align="center">
                          <Spin size="small" />
                          <Text type="secondary">Consultando status seguro do pagamento...</Text>
                        </Space>
                      ) : (
                        (() => {
                          const paymentInfo = paymentByRequestId[item.id];
                          if (!paymentInfo) {
                            return <Text type="secondary">Sem dados de pagamento disponiveis para esta solicitacao.</Text>;
                          }

                          const warningMessages = resolvePaymentWarningMessages(paymentInfo);
                          return (
                            <>
                              <Descriptions size="small" column={1}>
                                <Descriptions.Item label="Status do payment intent">
                                  {renderPaymentStateTag(paymentInfo.payment_state)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Status da cobranca">
                                  {renderChargeStatusTag(paymentInfo.charge_status)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Forma de pagamento">
                                  {BILLING_TYPE_LABEL[String(paymentInfo.billing_type || '').trim().toUpperCase()] || '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Metodo identificado">
                                  {PAYMENT_METHOD_LABEL[String(paymentInfo.payment_method || '').trim().toLowerCase()] || '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Status do link">
                                  {renderPaymentLinkStatusTag(paymentInfo.payment_link_status)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Proxima acao">
                                  {PAYMENT_NEXT_ACTION_LABEL[paymentInfo.next_action] || paymentInfo.next_action || '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Referencia do pagamento">
                                  {formatCompactRef(paymentInfo.provider_payment_ref)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Expiracao do link">
                                  {formatDateTime(paymentInfo.payment_url_expires_at)}
                                </Descriptions.Item>
                              </Descriptions>

                              <Alert
                                type={paymentInfo.payment_available ? 'success' : 'info'}
                                showIcon
                                style={{ marginTop: 12 }}
                                message={paymentInfo.payment_available ? 'Pagamento disponivel' : 'Pagamento indisponivel'}
                                description={getPaymentSummaryMessage(paymentInfo)}
                              />

                              {warningMessages.length > 0 ? (
                                <Space wrap style={{ marginTop: 12 }}>
                                  {warningMessages.map((warning) => (
                                    <Tag key={`${item.id}-${warning}`} color="gold">{warning}</Tag>
                                  ))}
                                </Space>
                              ) : null}

                              {resolvePreferredPaymentLink(paymentInfo) || paymentInfo.pix_payload ? (
                                <Space wrap style={{ marginTop: 12 }}>
                                  <Button
                                    type="primary"
                                    disabled={!resolvePreferredPaymentLink(paymentInfo)}
                                    onClick={() => handleOpenPayment(paymentInfo)}
                                  >
                                    Abrir pagamento
                                  </Button>
                                  {paymentInfo.boleto_url ? (
                                    <Button onClick={() => handleOpenAnyPaymentLink(paymentInfo.boleto_url)}>
                                      Abrir boleto
                                    </Button>
                                  ) : null}
                                  {paymentInfo.invoice_url ? (
                                    <Button onClick={() => handleOpenAnyPaymentLink(paymentInfo.invoice_url)}>
                                      Abrir link da fatura
                                    </Button>
                                  ) : null}
                                  {paymentInfo.pix_payload ? (
                                    <Button onClick={() => handleCopyPixPayload(paymentInfo.pix_payload)}>
                                      Copiar PIX (copia e cola)
                                    </Button>
                                  ) : null}
                                </Space>
                              ) : null}

                              {paymentInfo.pix_payload ? (
                                <Card size="small" style={{ marginTop: 12 }}>
                                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                    <Text strong>PIX copia e cola</Text>
                                    <Input.TextArea
                                      value={paymentInfo.pix_payload}
                                      autoSize={{ minRows: 2, maxRows: 6 }}
                                      readOnly
                                    />
                                  </Space>
                                </Card>
                              ) : null}
                            </>
                          );
                        })()
                      )}
                    </Card>

                    <Card
                      title="Ciclo simplificado da solicitacao"
                      size="small"
                      style={{ marginTop: 12 }}
                    >
                      {(() => {
                        const paymentInfo = paymentByRequestId[item.id];
                        const steps = buildTenantLifecycleSteps(item, paymentInfo);
                        const firstPending = steps.findIndex((step) => !step.done && !step.error);
                        const current = firstPending >= 0 ? firstPending : steps.length - 1;

                        return (
                          <Steps
                            size="small"
                            direction="vertical"
                            current={current}
                            items={steps.map((step) => ({
                              title: step.title,
                              description: step.description,
                              status: step.error ? 'error' : step.done ? 'finish' : 'process',
                            }))}
                          />
                        );
                      })()}
                    </Card>
                  </Card>
                );
              })}
              </Space>
            )}
          </Card>
        </Col>

      </Row>

      <Modal
        open={immediatePaymentModal.open}
        title="Pagamento da solicitacao de upgrade"
        onCancel={closeImmediatePaymentModal}
        footer={(
          <Space>
            <Button onClick={closeImmediatePaymentModal}>Fechar</Button>
            <Button onClick={refreshImmediatePaymentModal} loading={loading || paymentHistoryLoading || paymentRefreshLoading}>
              Ja paguei / atualizar status
            </Button>
          </Space>
        )}
      >
        <Descriptions size="small" column={1}>
          <Descriptions.Item label="Status financeiro">{renderPaymentStateTag(immediatePaymentInfo.payment_state)}</Descriptions.Item>
          <Descriptions.Item label="Status da cobranca">{renderChargeStatusTag(immediatePaymentInfo.charge_status)}</Descriptions.Item>
          <Descriptions.Item label="Metodo">
            {BILLING_TYPE_LABEL[String(immediatePaymentInfo.billing_type || '').trim().toUpperCase()]
              || PAYMENT_METHOD_LABEL[String(immediatePaymentInfo.payment_method || '').trim().toLowerCase()]
              || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Valor">
            {immediatePaymentInfo.amount_cents != null
              ? formatCurrencyCents(immediatePaymentInfo.amount_cents, immediatePaymentInfo.currency_code || 'BRL')
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Vencimento">{formatDateTime(immediatePaymentInfo.due_at)}</Descriptions.Item>
          <Descriptions.Item label="Referencia">{formatCompactRef(immediatePaymentInfo.provider_payment_ref)}</Descriptions.Item>
        </Descriptions>

        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message="Seu plano atual permanece ativo ate a confirmacao do pagamento."
        />

        <Space wrap style={{ marginTop: 12 }}>
          <Button
            type="primary"
            disabled={!resolvePreferredPaymentLink(immediatePaymentInfo)}
            onClick={() => handleOpenAnyPaymentLink(resolvePreferredPaymentLink(immediatePaymentInfo))}
          >
            Abrir boleto/link
          </Button>
          <Button
            disabled={!immediatePaymentInfo.pix_payload}
            onClick={() => handleCopyPixPayload(immediatePaymentInfo.pix_payload)}
          >
            Copiar PIX
          </Button>
        </Space>

        {immediatePaymentInfo.pix_payload ? (
          <Card size="small" style={{ marginTop: 12 }}>
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Text strong>PIX copia e cola</Text>
              <Input.TextArea
                value={immediatePaymentInfo.pix_payload}
                autoSize={{ minRows: 2, maxRows: 6 }}
                readOnly
              />
              {resolvePixQrImageSrc(immediatePaymentInfo) ? (
                <img
                  src={resolvePixQrImageSrc(immediatePaymentInfo)}
                  alt="QR Code PIX"
                  style={{ maxWidth: 240, width: '100%', borderRadius: 8 }}
                />
              ) : null}
            </Space>
          </Card>
        ) : null}
      </Modal>

      <Modal
        open={isModalOpen}
        title={`Solicitar ${CHANGE_REQUEST_LABEL[requestType] || 'alteracao'}`}
        onCancel={closeRequestModal}
        onOk={() => form.submit()}
        okText="Enviar solicitacao"
        okButtonProps={{
          disabled: (() => {
            const normalizedType = String(requestType || '').trim().toUpperCase();
            if (normalizedType === 'UPGRADE') {
              return upgradePlansLoading || upgradePlans.length === 0;
            }
            if (normalizedType === 'DOWNGRADE') {
              return downgradePlansLoading || downgradePlans.length === 0;
            }
            return false;
          })(),
        }}
        confirmLoading={submitLoading}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Fluxo de solicitacao"
          description={(() => {
            const normalizedType = String(requestType || '').trim().toUpperCase();
            if (normalizedType === 'DOWNGRADE') {
              return 'O downgrade sera agendado para o proximo ciclo. Seu plano atual continua ativo ate la.';
            }
            if (normalizedType === 'CANCEL') {
              return 'O cancelamento e agendado. O acesso permanece ativo ate a data de efetivacao e os dados nao sao apagados automaticamente.';
            }
            return 'Selecione plano e forma de pagamento. O plano atual permanece ativo ate a confirmacao do pagamento.';
          })()}
        />
        <Form form={form} layout="vertical" onFinish={submitRequest}>
          <Form.Item name="request_type" hidden>
            <Input />
          </Form.Item>

          {String(requestType || '').trim().toUpperCase() === 'UPGRADE' ? (
            <>
              <Form.Item
                label="Plano desejado"
                name="requested_plan_code"
                rules={[{ required: true, message: 'Selecione um plano para continuar.' }]}
              >
                <Radio.Group style={{ width: '100%' }}>
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    {upgradePlans.map((plan) => {
                      const code = String(plan?.code || '').trim();
                      const name = String(plan?.name || '').trim() || code;
                      const highlights = Array.isArray(plan?.highlights) ? plan.highlights.filter(Boolean) : [];
                      return (
                        <Card
                          key={code}
                          size="small"
                          style={{ width: '100%' }}
                          bodyStyle={{ padding: 12 }}
                        >
                          <Space direction="vertical" size={6} style={{ width: '100%' }}>
                            <Radio value={code}>{name}</Radio>
                            <Text type="secondary">{`Codigo: ${code}`}</Text>
                            <Text strong>
                              {formatCurrencyCents(plan?.base_price_cents, plan?.currency_code)}
                              {` / ${plan?.billing_period_months || 1} mes(es)`}
                            </Text>
                            {Number(plan?.trial_days_default || 0) > 0 ? (
                              <Text type="secondary">{`Trial padrao: ${plan.trial_days_default} dias`}</Text>
                            ) : null}
                            {highlights.length > 0 ? (
                              <Space wrap>
                                {highlights.slice(0, 4).map((item) => (
                                  <Tag key={`${code}-${item}`} color="blue">{item}</Tag>
                                ))}
                              </Space>
                            ) : (
                              <Text type="secondary">Sem beneficios adicionais destacados.</Text>
                            )}
                          </Space>
                        </Card>
                      );
                    })}
                  </Space>
                </Radio.Group>
              </Form.Item>
              {upgradePlansLoading ? (
                <Space align="center">
                  <Spin size="small" />
                  <Text type="secondary">Carregando planos elegiveis...</Text>
                </Space>
              ) : null}
              {!upgradePlansLoading && upgradePlans.length === 0 ? (
                <Alert
                  type="warning"
                  showIcon
                  message="Nenhum plano elegivel encontrado"
                  description={upgradePlansError || 'Nao ha planos padrao/publicos disponiveis para upgrade neste momento.'}
                />
              ) : null}

              <Form.Item
                label="Forma de pagamento"
                name="billing_type"
                rules={[{ required: true, message: 'Selecione PIX ou Boleto.' }]}
              >
                <Radio.Group>
                  <Space direction="vertical" size={8}>
                    <Radio value="PIX">PIX</Radio>
                    <Radio value="BOLETO">Boleto</Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>
            </>
          ) : null}

          {String(requestType || '').trim().toUpperCase() === 'DOWNGRADE' ? (
            <>
              <Form.Item
                label="Plano desejado"
                name="requested_plan_code"
                rules={[{ required: true, message: 'Selecione um plano para continuar.' }]}
              >
                <Radio.Group style={{ width: '100%' }}>
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    {downgradePlans.map((plan) => {
                      const code = String(plan?.code || '').trim();
                      const name = String(plan?.name || '').trim() || code;
                      const highlights = Array.isArray(plan?.highlights) ? plan.highlights.filter(Boolean) : [];
                      return (
                        <Card
                          key={code}
                          size="small"
                          style={{ width: '100%' }}
                          bodyStyle={{ padding: 12 }}
                        >
                          <Space direction="vertical" size={6} style={{ width: '100%' }}>
                            <Radio value={code}>{name}</Radio>
                            <Text type="secondary">{`Codigo: ${code}`}</Text>
                            <Text strong>
                              {formatCurrencyCents(plan?.base_price_cents, plan?.currency_code)}
                              {` / ${plan?.billing_period_months || 1} mes(es)`}
                            </Text>
                            {highlights.length > 0 ? (
                              <Space wrap>
                                {highlights.slice(0, 4).map((item) => (
                                  <Tag key={`${code}-${item}`} color="geekblue">{item}</Tag>
                                ))}
                              </Space>
                            ) : (
                              <Text type="secondary">Sem beneficios adicionais destacados.</Text>
                            )}
                          </Space>
                        </Card>
                      );
                    })}
                  </Space>
                </Radio.Group>
              </Form.Item>
              {downgradePlansLoading ? (
                <Space align="center">
                  <Spin size="small" />
                  <Text type="secondary">Carregando planos elegiveis...</Text>
                </Space>
              ) : null}
              {!downgradePlansLoading && downgradePlans.length === 0 ? (
                <Alert
                  type="warning"
                  showIcon
                  message="Nenhum plano elegivel encontrado"
                  description={downgradePlansError || 'Nao ha planos padrao/publicos disponiveis para downgrade neste momento.'}
                />
              ) : null}
            </>
          ) : null}

          {String(requestType || '').trim().toUpperCase() === 'CANCEL' ? (
            <>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message="Confirmacao de cancelamento agendado"
                description="O cancelamento sera efetivado no proximo ciclo elegivel. Tenant, usuarios e dados operacionais permanecem preservados conforme politica de retencao e auditoria."
              />
              <Form.Item
                label="Motivo (opcional)"
                name="reason"
              >
                <Input.TextArea
                  placeholder="Descreva o motivo do cancelamento (opcional)."
                  autoSize={{ minRows: 3, maxRows: 5 }}
                  maxLength={500}
                  showCount
                />
              </Form.Item>
            </>
          ) : null}
        </Form>
      </Modal>
    </div>
  );
};

export default TenantAccountSubscriptionPage;
